import {
  useQueryClient,
  useQuery,
  useMutation,
  keepPreviousData,
} from "@tanstack/react-query";
import { errorMessage, successMessage } from "../../../utils/helper";
import {
  assignMactoStore,
  assignRecipeToStoreApi,
  deleteRecipeFromStoreApi,
  getStoreById,
  getStoreListWithMac,
  getStoreMappedRecipe,
  unAssignMactoStore,
} from "../api/storesApi";
import qs from "qs";

const storesKeys = {
  all: ["stores"],
  lists: () => [...storesKeys.all, "storeslist"],
  detail: (id) => [...storesKeys.lists(), id],
  mappedRecipe: (id) => [...storesKeys.detail(id), "mappedRecipe"],
};

export const useStoresQueries = ({
  isList,
  storeId,
  selectedType,
  selectedStatus,
  debouncedText,
}) => {
  const queryClient = useQueryClient();
  let params = {};
  if (debouncedText) {
    params = { ...params, search: debouncedText };
  }

  if (selectedType !== "") {
    params = { ...params, type: selectedType };
  }
  if (selectedStatus !== "") {
    params = { ...params, status: selectedStatus };
  }

  const query = {
    params,
    paramsSerializer: (params) => qs.stringify(params, { encode: true }),
  };

  //list useQueryClient
  const listQuery = useQuery({
    queryKey: storesKeys.lists(),
    queryFn: () => getStoreListWithMac(),
    placeholderData: keepPreviousData,
    enabled: isList,
  });

  const assignMac = useMutation({
    mutationFn: ({ _, data }) => assignMactoStore(data),
    onError: (error) => {
      errorMessage(error.response.data);
    },
    onSuccess: (res, variables) => {
      if (res.success) {
        queryClient.invalidateQueries({
          queryKey: storesKeys.detail(storeId),
        });
        successMessage(res.message);
      } else if (res.success === false) {
        errorMessage(res.message);
      }
      variables.handleModal();
    },
  });

  const storeDetails = useQuery({
    queryKey: storesKeys.detail(storeId),
    queryFn: () => getStoreById(storeId),
    enabled: storeId > 0,
  });

  const unAssignMac = useMutation({
    mutationFn: ({ _, data }) => unAssignMactoStore(data),
    onError: (error) => {
      errorMessage(error?.response?.data);
    },
    onSuccess: (res, variables) => {
      if (res.success) {
        queryClient.invalidateQueries({
          queryKey: storesKeys.detail(storeId),
        });
        successMessage(res.message);
      } else if (res.success === false) {
        errorMessage(res.message);
      }
      variables.handleModal();
    },
  });

  const storeMappedRecipe = useQuery({
    queryKey: storesKeys.mappedRecipe({ storeId, isAllList: false, query }),
    queryFn: () => getStoreMappedRecipe({ storeId, isAllList: false, query }),
    enabled: storeId > 0,
  });

  const storeMappedAllRecipe = useQuery({
    queryKey: storesKeys.mappedRecipe({ storeId, isAllList: true, query }),
    queryFn: () => getStoreMappedRecipe({ storeId, isAllList: true, query }),
    enabled: storeId > 0,
  });

  const assignRecipeToStore = useMutation({
    mutationFn: ({ _, data }) => assignRecipeToStoreApi({ storeId, data }),
    onError: (error) => {
      errorMessage(error?.response?.data);
    },
    onSuccess: (res, variables) => {
      if (res.success) {
        queryClient.invalidateQueries({
          queryKey: storesKeys.mappedRecipe({ storeId }),
        });
        successMessage(res.message);
      }
      variables.handleModal();
    },
  });
  const deleteRecipeFromStore = useMutation({
    mutationFn: ({ _, recipeId, data }) =>
      deleteRecipeFromStoreApi({ storeId, recipeId, data }),
    onError: (error) => {
      errorMessage(error?.response?.data);
    },
    onSuccess: (res) => {
      if (res.success) {
        queryClient.invalidateQueries({
          queryKey: storesKeys.mappedRecipe({ storeId }),
        });
        successMessage(res.message);
      }
    },
  });
  return {
    listQuery,
    storeDetails,
    unAssignMac,
    assignMac,
    storeMappedRecipe,
    assignRecipeToStore,
    deleteRecipeFromStore,
    storeMappedAllRecipe,
  };
};
